import { useCallback, useContext } from 'react';
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { saveFormCampaign } from 'Api/Campaigns/campaignsApi';
import { markCampaignsAsStale } from '../features/campaigns/campaignsReducer';
import { setAndShowErrorToast } from '../store/config';
import { FormConfigType } from '@urbanx/agx-ui-components';
import { saveFormMarketing } from 'Api/Marketing/marketingApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useCampaignStaleMarker } from './useCampaignStaleMarker';

/**
 * Convert a Date to YYYY-MM-DD so the calendar date is preserved when saved (avoids UTC shift).
 */
const toDateOnlyString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * Deep-clone form data and convert Date values to YYYY-MM-DD strings so saved values
 * preserve the calendar date (e.g. "2026-02-21") instead of UTC ISO ("2026-02-20T14:00:00.000Z").
 */
export const normalizeFormDates = <T,>(data: T): T => {
  if (data instanceof Date) {
    return toDateOnlyString(data) as T;
  }
  if (Array.isArray(data)) {
    return data.map(normalizeFormDates) as unknown as T;
  }
  if (data != null && typeof data === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) {
      out[k] = normalizeFormDates(v);
    }
    return out as unknown as T;
  }
  return data;
};

export const useFormSaver = () => {
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const markCampaignAsStale = useCampaignStaleMarker();
  const {
    campaignId,
    formId,
    selectedForm,
    formValues: formData,
    pageStatuses,
  } = useAppSelector(state => state.form);
  const { formType, state } = selectedForm || {};

  const saveFormData = useCallback(
    async (formConfigType?: FormConfigType) => {
      let errorMessage: string = '';
      switch (formConfigType) {
        case FormConfigType.Campaign: {
          errorMessage = 'Failed to save form campaign';
          break;
        }
        case FormConfigType.Marketing: {
          errorMessage = 'Failed to save form marketing package';
          break;
        }
        default: {
          errorMessage = 'Failed to save form';
        }
      }
      if (!getAuthToken) return;

      if (!campaignId || !formId || !formType || !state) {
        dispatch(setAndShowErrorToast(errorMessage));
        return;
      }

      try {
        const authToken = await getAuthToken();

        if (!authToken) {
          dispatch(setAndShowErrorToast(errorMessage));
          return;
        }

        const formDataToSave = normalizeFormDates(formData);

        switch (formConfigType) {
          case FormConfigType.Campaign: {
            const formSaved = await saveFormCampaign(
              authToken,
              campaignId,
              formId,
              formType,
              state,
              formDataToSave,
              pageStatuses
            );

            if (formSaved) {
              dispatch(markCampaignsAsStale());
              void markCampaignAsStale(campaignId).catch(console.error);
            }
            break;
          }
          case FormConfigType.Marketing: {
            await saveFormMarketing(
              authToken,
              campaignId,
              formId,
              formType,
              state,
              formDataToSave,
              pageStatuses
            );
            break;
          }
        }
      } catch (err: any) {
        console.error(err);
        dispatch(setAndShowErrorToast(err.message));
      }
    },
    [
      campaignId,
      dispatch,
      formData,
      formId,
      formType,
      getAuthToken,
      markCampaignAsStale,
      pageStatuses,
      state,
    ]
  );

  return saveFormData;
};
